import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate, ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import { Divider, Grid, Paper, TextField } from '@mui/material'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { DataGrid, GridColDef, GridValueFormatterParams } from '@mui/x-data-grid'
import { formatDate } from 'date-fns'
import { formatCPFOrCNPJ } from 'src/utils/formatter-utils'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import { StatusProcessamentoBordero } from '../../bordero.types'
import StatusProcessamentoScope from '../status_processamento.scopes'

const LOG = Logger.get('STATUS-PROCESSAMENTO-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    switch: {
        zoom: 0.6
    },
    hbox: {
        display: 'flex',
        flexDirection: 'row'
    },
    vbox: {
        display: 'flex',
        flexDirection: 'column'
    },
    syncButton: {
        alignSelf: 'center'
    }
})

const columns = static_build_columns()

type StatusProcessamentoViewProps = {
    className?: string
    scope: StatusProcessamentoScope
}
class StatusProcessamentoViewClass implements FCClassContext<StatusProcessamentoViewProps> {
    // :: Fields
    scope!: StatusProcessamentoScope
    sytles!: ReturnType<typeof useStyles>
    filtroInputRef!: React.MutableRefObject<HTMLInputElement | undefined>

    // :: emissors
    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitOnFind = () => {
        const filtroValue = this.filtroInputRef.current?.value || ''
        this.scope.onFind(filtroValue).catch(LOG.caught)
    }
    // :: Methods

    onSyncState({ scope }: StatusProcessamentoViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: StatusProcessamentoViewProps) {
        this.scope = bindUpdate(React, scope)
        this.filtroInputRef = React.useRef<HTMLInputElement>()

        const classes = this.sytles.classes

        if (scope.waiting) {
            return <ViewSlot scope={scope.waiting} />
        }

        return (
            <div className={clsx(classes.view, className)}>
                <Paper elevation={3} style={{ padding: 10, minWidth: 800, height: 800 }}>
                    <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px">
                        <TextField
                            className={classes.cnpjInput}
                            inputRef={this.filtroInputRef}
                            label="Filtre pela Etapa, Status ou Referência"
                            variant="standard"
                        />
                        <Button variant="outlined" onClick={this.emitOnFind}>
                            Buscar
                        </Button>
                    </Box>
                    <Grid container spacing={1} paddingTop={2} paddingBottom={2} paddingLeft={5} paddingRight={5}>
                        <Grid item xs={12}>
                            <DataGrid
                                rows={this.scope.resultados}
                                columns={columns}
                                getRowId={static_getRowId}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Divider />

                    <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px" marginTop="10px">
                        <Button variant="outlined" onClick={this.emitOnClose}>
                            Fechar
                        </Button>
                    </Box>
                </Paper>
            </div>
        )
    }
}

export const StatusProcessamentoView = classToFComponent(StatusProcessamentoViewClass, React)
export default StatusProcessamentoView

function static_build_columns(): GridColDef[] {
    return [
        {
            field: 'etapa',
            headerName: 'Etapa',
            width: 150
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120
        },
        {
            field: 'ref',
            headerName: 'Referência',
            width: 140,
            valueGetter(params) {
                return params.row.tipo_ref === 'documento' ? formatCPFOrCNPJ(params.row.ref) : params.row.ref
            }
        },
        {
            field: 'created_at',
            headerName: 'Data Início',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (params: GridValueFormatterParams<string>) => {
                return !params.value ? '' : formatDate(params.value, 'dd/MM/yyyy HH:mm')
            }
        },
        {
            field: 'updated_at',
            headerName: 'Data Fim',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (params: GridValueFormatterParams<string>) => {
                return !params.value ? '' : formatDate(params.value, 'dd/MM/yyyy HH:mm')
            }
        }
    ]
}

function static_getRowId(row: StatusProcessamentoBordero) {
    return row.etapa + row.ref
}
